import { CardContent, Stack } from '@mui/material';
import { type RestoreGcpInstanceDiskInput, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { GcpEncryptionComponent } from '@/components/encryption/gcpEncryptionComponent';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { SingleGcpDiskConfig } from './singleGcpDiskConfig';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpVMState } from '../../restoreGcpVMWizard';
import { EonTagRemark } from '../eonTagRemark';

export const GcpVmDiskConfiguration = (props: StepProps<RestoreGcpVMState>) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const startRestore = () => {
    setIsRestoring(true);
    setError(undefined);

    const labels = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon-restore': 'true',
    };

    const diskSettings = props.wizardState.disksState?.map(
      (disk): RestoreGcpInstanceDiskInput => ({
        providerDiskId: disk.diskSettings.diskId,
        settings: {
          name: disk.diskSettings.diskName,
          type: disk.diskSettings.diskType,
          sizeBytes: disk.diskSettings.diskSizeBytes,
          labels: {
            ...(props.wizardState.keepOriginalTags
              ? disk.initialTags
              : disk.tags),
            'eon-restore': 'true',
          },
          encryptionKeyId: props.wizardState.sharedEncryption.isEncrypted
            ? props.wizardState.sharedEncryption.encryptionKeyId
            : disk.encryptionKey,
          iops: disk.diskSettings.iops,
          throughput: disk.diskSettings.throughput,
        },
      })
    );

    return dal.restore.gcpVm
      .restoreVm(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          gcpVm: {
            zone: props.wizardState.zone!,
            machineType: props.wizardState.machineType ?? '',
            name: props.wizardState.vmName ?? '',
            networkName: props.wizardState.network?.networkName ?? '',
            subnetName: props.wizardState.subnetName ?? '',
            networkHostProject: props.wizardState.network?.project ?? '',
            labels: labels,
            disks: diskSettings,
            startInstanceAfterRestore:
              props.wizardState.startInstanceAfterRestore,
          },
        },
      })
      .then(() =>
        props.finish(`Started restore job for ${props.wizardState.vmName}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore disk: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark provider={Provider.Gcp} />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() =>
        !isRestoring &&
        !!props.wizardState.vmName &&
        !!props.wizardState.regionName &&
        !!props.wizardState.subnetName &&
        !!props.wizardState.zone &&
        !!props.wizardState.machineType &&
        (props.wizardState.sharedEncryption.isEncrypted
          ? !!props.wizardState.sharedEncryption.encryptionKeyId
          : true)
      }
      onNextClick={startRestore}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <CardContent className='p-0'>
        <GcpEncryptionComponent
          cloudAccountId={props.wizardState.restoreAccount?.id}
          regionName={props.wizardState.regionName}
          onChange={(isEncrypted, encryptionKeyId) => {
            props.setWizardState((state) => ({
              ...state,
              sharedEncryption: {
                isEncrypted,
                encryptionKeyId,
              },
            }));
          }}
          title='Encrypt all disks with the same key'
          initialIsEncrypted={props.wizardState.sharedEncryption.isEncrypted}
        />
      </CardContent>
      <CardContent className='px-0'>
        {props.wizardState.disksState?.map((disk) => {
          const initialDiskSettings = props.wizardState.initialDiskStates.find(
            (d) => disk.diskSettings.diskId === d.diskSettings.diskId
          );
          if (
            !initialDiskSettings ||
            !props.wizardState.restoreAccount ||
            !props.wizardState.regionName
          ) {
            return null;
          }
          return (
            <SingleGcpDiskConfig
              key={disk.diskSettings.diskId}
              diskId={disk.diskSettings.diskId}
              accountID={props.wizardState.restoreAccount.id}
              zone={props.wizardState.zone ?? ''}
              region={props.wizardState.regionName ?? ''}
              machineType={props.wizardState.machineType}
              initialSettings={initialDiskSettings}
              diskSettings={disk}
              setWizardState={props.setWizardState}
              showEncryption={!props.wizardState.sharedEncryption.isEncrypted}
            />
          );
        })}
      </CardContent>
    </StepContainer>
  );
};
